/**
 * strokeInsights — "Your strokes · per 25" (Scores tab, 2026-09-01; owner's
 * ask: "stroke insights with trends and fastest sets / stroke counts per 25").
 *
 * Pure. Input: the swimFacts v3 cache entries (each carrying a per-stroke
 * block over its record-eligible lengths — see allTimeAggregates
 * .SwimFactsStrokeSummary). Output: one row per stroke actually swum, IM
 * order (fly, back, breast, free). Kickboard / Mixed / Unknown are not
 * strokes and never get a card.
 *
 * Only 25 m pool swims count: a 20 m length is not a 25 and open water has no
 * lengths, so those swims are simply not in this section (the label says
 * "per 25"). Carry-overs still waiting on the v3 re-extract (strokes: null)
 * are skipped; the section's coverage line says how many.
 *
 * Numbers (every one recountable by hand — no indices, no weighting):
 *   typical25  median of the per-swim medians over the SAME last-10
 *              qualifying swims the trend reads (≥ TREND_MIN_LENGTHS eligible
 *              lengths), so the headline sits between the two trend windows.
 *              With fewer than 10 qualifying swims it uses what qualifies
 *              (`typicalSwims` says how many); with none, the last 10 swims.
 *   best25     the fastest record-eligible length ever — ONE observed length,
 *              re-checked against the mature all-history floor (earliest swim
 *              keeps a tie)
 *   typicalSpl median of the per-swim mean strokes-per-length, same window
 *              (no "fewest strokes": a stroke count is not a standalone
 *              record — a 4-stroke butterfly length is a glitch)
 *   restRange  typical rest before a length of this stroke: the median lower
 *              and upper quartile across the same window's swims with rests
 *   series     per-swim median of the QUALIFYING swims only, oldest → newest,
 *              newest MAX_BARS kept: a swim that does not count toward the
 *              trend gets no bar, so the rightmost 5 bars are the 5 compared
 *
 * Trend (steering-committee rule, 2026-09-01): a swim QUALIFIES for the trend
 * only with ≥ TREND_MIN_LENGTHS eligible lengths of the stroke. With fewer
 * than TREND_MIN_SWIMS qualifying swims there is no verdict. Otherwise
 * compare the median of the last 5 qualifying swims with the median of the 5
 * before; "faster" / "slower" ONLY when |Δ| clears the MEASURED cross-watch
 * noise floor for that stroke (noiseFloor.TREND_FLOOR_S), else "steady" —
 * a positive claim, not a shrug. The evidence (both medians and the lengths
 * behind them) is carried on the row so the card never shows a bare word.
 */
import type { SwimFactsEntry } from "./allTimeAggregates";
import {
  medianDurationByStroke,
  recordFloorSeconds,
  weightedMedian,
  type MedianByStroke,
} from "./recordEligibility";
import { trendFloorS, isTrendFloorMeasured } from "./noiseFloor";
import { IM_ORDER } from "./setLibrary";
import { fmtSeconds1 } from "./format";

export const TYPICAL_WINDOW = 10;
export const TREND_WINDOW = 5;
/** Qualifying swims needed for a verdict: last 5 vs the 5 before. */
/** Round to the tenth the cards print, so every shown number subtracts cleanly. */
const r1 = (x: number): number => Math.round(x * 10) / 10;

export const TREND_MIN_SWIMS = 2 * TREND_WINDOW;
/** Eligible lengths of the stroke a swim needs to count toward the trend. */
export const TREND_MIN_LENGTHS = 6;
/** Past this many swims the bars stop being readable at 390 px (YourSetsCard). */
export const MAX_BARS = 30;

export type StrokeTrend = "faster" | "slower" | "steady";

export interface StrokeSwimPoint {
  /** This swim's median length of the stroke, seconds. */
  medianS: number;
  /** Local midnight of the swim's date, epoch ms. */
  dateMs: number;
  workoutId: string;
}

export interface StrokeBest25 {
  seconds: number;
  dateMs: number;
  workoutId: string;
}

export interface StrokeTrendEvidence {
  /** Median of the last TREND_WINDOW qualifying swims' medians. */
  lastMedianS: number;
  /** Median of the TREND_WINDOW qualifying swims before those. */
  prevMedianS: number;
  /** prev − last: positive = quicker lately. */
  deltaS: number;
  /** Eligible lengths behind each side; the smaller of the two sides. */
  lengthsPerSide: number;
  /** The noise floor the delta had to clear, seconds. */
  floorS: number;
  /** False when floorS is the unmeasured fallback (copy calls it a guess). */
  measured: boolean;
  /** The swims behind each side, oldest → newest; the card brightens their bars. */
  lastWorkoutIds: string[];
  prevWorkoutIds: string[];
}

export interface StrokeInsightRow {
  stroke: string;
  /** 25 m pool swims with at least one record-eligible length of this stroke. */
  swims: number;
  /** Swims with ≥ TREND_MIN_LENGTHS eligible lengths — the ones the trend may use. */
  qualifyingSwims: number;
  typical25: number;
  /** Swims behind typical25 / typicalSpl / restRange (≤ TYPICAL_WINDOW). */
  typicalSwims: number;
  best25: StrokeBest25 | null;
  typicalSpl: number | null;
  /** null until TREND_MIN_SWIMS qualifying swims. */
  trend: StrokeTrend | null;
  /** null with the trend. */
  evidence: StrokeTrendEvidence | null;
  /** Typical rest before a length of this stroke, seconds; null when no recent swim recorded rests. */
  restRange: { p25: number; p75: number } | null;
  /** Qualifying swims only, oldest → newest, at most MAX_BARS (the newest). */
  series: StrokeSwimPoint[];
}

/** "YYYY-MM-DD" → local midnight, so the day never shifts across UTC. */
function localDayMs(isoDay: string): number {
  const [y, m, d] = isoDay.split("-").map(Number);
  return new Date(y, m - 1, d).getTime();
}

const median = (xs: number[]): number | null => weightedMedian(xs.map((v) => ({ v, w: 1 })));

/** The 25 m pool swims with at least one length of this stroke: the one population
 *  the Strokes tab counts (the card's "N swims", the Fastest 25s sheet, the glide
 *  trend; VP panel round 2, 2026-09-11, read 92 / 87 / 98 as a contradiction). */
export function swims25(entries: SwimFactsEntry[], stroke: string): SwimFactsEntry[] {
  return entries.filter((e) => e.poolLengthMeters === 25 && e.strokes !== null && (e.strokes[stroke]?.n ?? 0) > 0);
}

function buildRow(stroke: string, entries: SwimFactsEntry[], medians: MedianByStroke): StrokeInsightRow | null {
  const swims = swims25(entries, stroke).sort(
    (a, b) => a.swimDate.localeCompare(b.swimDate) || a.swimUuid.localeCompare(b.swimUuid),
  );
  if (swims.length === 0) return null;

  const qualifying = swims.filter((e) => e.strokes![stroke].n >= TREND_MIN_LENGTHS);
  // One bar per swim that counts (orch-8 ruling, reversible, 2026-09-11): thin
  // swims drawn as bars pushed the 5 compared swims to mid-chart, under a tap
  // that calls them "the last 5".
  const bars: StrokeSwimPoint[] = qualifying.slice(-MAX_BARS).map((e) => ({
    medianS: e.strokes![stroke].medianS,
    dateMs: localDayMs(e.swimDate),
    workoutId: e.swimUuid,
  }));
  const recent = (qualifying.length > 0 ? qualifying : swims).slice(-TYPICAL_WINDOW);
  const typical25 = r1(median(recent.map((e) => e.strokes![stroke].medianS))!);

  // The Fastest 25s sheet's top row, so the tile and the sheet can never disagree.
  const best25 = fastest25s(swims, stroke, medians)[0] ?? null;
  const typicalSpl = median(
    recent.map((e) => e.strokes![stroke].meanSpl).filter((v): v is number => v !== null),
  );

  const rests = recent.map((e) => e.strokes![stroke].restS).filter((r): r is NonNullable<typeof r> => r !== null);
  const restRange =
    rests.length === 0 ? null : { p25: median(rests.map((r) => r.p25))!, p75: median(rests.map((r) => r.p75))! };

  let trend: StrokeTrend | null = null;
  let evidence: StrokeTrendEvidence | null = null;
  if (qualifying.length >= TREND_MIN_SWIMS) {
    const last = qualifying.slice(-TREND_WINDOW);
    const prev = qualifying.slice(-2 * TREND_WINDOW, -TREND_WINDOW);
    // Both window medians are rounded to the tenth the card prints, and the delta is
    // taken from THOSE, so a swimmer's own subtraction of the two shown numbers always
    // matches the "so X.Xs is within watch accuracy" sentence (VP re-gate B-A, 2026-09-01).
    const lastMedianS = r1(median(last.map((e) => e.strokes![stroke].medianS))!);
    const prevMedianS = r1(median(prev.map((e) => e.strokes![stroke].medianS))!);
    const deltaS = r1(prevMedianS - lastMedianS);
    const floorS = trendFloorS(stroke);
    const lengths = (xs: SwimFactsEntry[]) => xs.reduce((a, e) => a + e.strokes![stroke].n, 0);
    evidence = {
      lastMedianS,
      prevMedianS,
      deltaS,
      lengthsPerSide: Math.min(lengths(last), lengths(prev)),
      floorS,
      measured: isTrendFloorMeasured(stroke),
      lastWorkoutIds: last.map((e) => e.swimUuid),
      prevWorkoutIds: prev.map((e) => e.swimUuid),
    };
    trend = deltaS > floorS ? "faster" : deltaS < -floorS ? "slower" : "steady";
  }

  return {
    stroke,
    swims: swims.length,
    qualifyingSwims: qualifying.length,
    typical25,
    typicalSwims: recent.length,
    best25,
    typicalSpl,
    trend,
    evidence,
    restRange,
    series: bars,
  };
}

/** One row per stroke swum, IM order. */
export function strokeInsightRows(
  entries: SwimFactsEntry[],
  medians: MedianByStroke = medianDurationByStroke(entries),
): StrokeInsightRow[] {
  const rows: StrokeInsightRow[] = [];
  for (const stroke of IM_ORDER) {
    const row = buildRow(stroke, entries, medians);
    if (row) rows.push(row);
  }
  return rows;
}

/** One row of "Fastest 25s · <Stroke>": one swim's fastest length of the stroke. */
export type Fastest25Row = StrokeBest25;

/**
 * "Fastest 25s · <Stroke>" (brief item 4, 2026-09-11): each 25 m swim's cached
 * best length of the stroke, ONE per swim, fastest first, the earlier swim
 * first on a tie. The same record floor as the Best 25 tile, so the top row IS
 * the tile's number and a split-length glitch never ranks. No schema change:
 * it reads SwimFactsEntry.strokes[stroke].bestS.
 */
export function fastest25s(
  entries: SwimFactsEntry[],
  stroke: string,
  medians: MedianByStroke = medianDurationByStroke(entries),
): Fastest25Row[] {
  const floor = recordFloorSeconds(stroke, 25, medians);
  return swims25(entries, stroke)
    .filter((e) => e.strokes![stroke].bestS >= floor)
    .map((e) => ({ seconds: e.strokes![stroke].bestS, dateMs: localDayMs(e.swimDate), workoutId: e.swimUuid }))
    .sort((a, b) => a.seconds - b.seconds || a.dateMs - b.dateMs || a.workoutId.localeCompare(b.workoutId));
}

// ─── Copy helpers (one source of truth for the card and tests) ──────────────

/** "34 swims" / "1 swim": a count beside the stroke name, not a sentence. */
export function strokeSwimsLabel(row: Pick<StrokeInsightRow, "swims">): string {
  return row.swims === 1 ? "1 swim" : `${row.swims} swims`;
}

/** "5s" / "1.5s" — the floor, whole when it is whole. */
function fmtFloor(s: number): string {
  return Number.isInteger(s) ? `${s}s` : `${s.toFixed(1)}s`;
}

/**
 * The card's one trend tag, always on the card face (steering round 3,
 * 2026-09-11): "Steady", "Faster 1.7s", "Slower 1.7s", or "4/10 swims" until
 * there is a verdict. Neutral words, no arrows; the reasoning sits behind the
 * card's "How it's counted" tap.
 */
export function strokeTrendTag(row: Pick<StrokeInsightRow, "trend" | "qualifyingSwims" | "evidence">): string {
  if (row.trend === null || row.evidence === null) return `${row.qualifyingSwims}/${TREND_MIN_SWIMS} swims`;
  if (row.trend === "steady") return "Steady";
  return `${row.trend === "faster" ? "Faster" : "Slower"} ${fmtSeconds1(Math.abs(row.evidence.deltaS))}s`;
}

/** "20+ lengths" beside the tag, so a verdict on 6 lengths never looks as sure
 *  as one on 20+ (the bucket keeps its "+"). null without a verdict. */
export function strokeLengthsMark(row: Pick<StrokeInsightRow, "evidence">): string | null {
  return row.evidence ? `${lengthsBucket(row.evidence.lengthsPerSide)}+ lengths` : null;
}

/**
 * The verdict sentence WITH its rule inline, so "same pace" above a 1.7 s
 * gap never reads as the card contradicting itself (VP panel B1, 5/5).
 * Lives behind "How it's counted"; the word "noise" is never used (a swimmer
 * read it as "my swim got thrown out"). Two sentences max.
 */
export function strokeVerdictLine(
  row: Pick<StrokeInsightRow, "stroke" | "trend" | "qualifyingSwims" | "evidence">,
): string {
  const name = row.stroke.toLowerCase();
  if (row.trend === null || row.evidence === null) {
    return `Not enough swims of this stroke yet to call a trend (${row.qualifyingSwims} of ${TREND_MIN_SWIMS}).`;
  }
  const e = row.evidence;
  const gap = `${fmtSeconds1(Math.abs(e.deltaS))}s`;
  const floor = fmtFloor(e.floorS);
  const guess = e.measured ? "" : ` (no two-watch ${name} swim yet, so this floor is a guess)`;
  if (row.trend === "steady") {
    return `Steady. ${row.stroke} can swing up to ${floor} between two watches on the same body${guess}, so ${gap} is within watch accuracy.`;
  }
  return `${row.trend === "faster" ? "Faster" : "Slower"} by ${gap}, more than the ${floor} two watches disagree by${guess}.`;
}

/**
 * Whether the card splits its bars bright / half / faint: only with a verdict
 * AND every one of the last 5 compared swims on the chart. Bars are qualifying
 * swims only, so today the compared swims are always the rightmost bars; the
 * check keeps a verdict tag from ever sitting over all-faint bars if the two
 * ever drift apart.
 */
export function strokeBarsSplit(row: Pick<StrokeInsightRow, "evidence" | "series">): boolean {
  if (!row.evidence) return false;
  const onChart = new Set(row.series.map((p) => p.workoutId));
  return row.evidence.lastWorkoutIds.every((id) => onChart.has(id));
}

/** What the bar brightness means; the card says it only when `strokeBarsSplit`. */
export const STROKES_BARS_LINE = `Bright bars are the last ${TREND_WINDOW} swims the trend compares, half-bright the ${TREND_WINDOW} before, faint the rest.`;

/** The evidence line names the smaller side's length count floored to a
 *  bucket — 6+ / 10+ / 20+ — a scale a reader can hold, not a precise count. */
export const LENGTHS_BUCKETS = [20, 10, 6] as const;
export function lengthsBucket(n: number): number {
  return LENGTHS_BUCKETS.find((b) => n >= b) ?? LENGTHS_BUCKETS[LENGTHS_BUCKETS.length - 1];
}

/** "Last 5: 33.1s · 5 before: 34.6s · 20+ lengths each" — beside every verdict. null without one. */
export function strokeEvidenceLine(row: Pick<StrokeInsightRow, "evidence">): string | null {
  const e = row.evidence;
  if (!e) return null;
  return `Last ${TREND_WINDOW}: ${fmtSeconds1(e.lastMedianS)}s · ${TREND_WINDOW} before: ${fmtSeconds1(e.prevMedianS)}s · ${lengthsBucket(e.lengthsPerSide)}+ lengths each`;
}

/** "Usual rest before a length: 15–30s." — a sentence, not the orphan fragment
 *  "reps on 15–30s rest" (VP panel round 2, 2026-09-11).
 *  null when unknown, and null when the two quartiles round to the same
 *  number (a single value is not a range). */
export function strokeRestLine(row: Pick<StrokeInsightRow, "restRange">): string | null {
  const r = row.restRange;
  if (!r) return null;
  const lo = Math.round(r.p25);
  const hi = Math.round(r.p75);
  return lo === hi ? null : `Usual rest before a length: ${lo}–${hi}s.`;
}

/** What the card's numbers and bars mean. Inside each card's "How it's counted"
 *  tap, never on the card face (steering round 3, 2026-09-11). */
export const STROKES_SECTION_EXPLAINER =
  "Typical 25 is the middle value of your swims, so one odd length can't move it. " +
  "Strokes per 25 is how many arm strokes a length took; fewer usually means a longer glide. " +
  `Each bar is one swim with ${TREND_MIN_LENGTHS}+ lengths of the stroke, its typical 25, oldest to newest. Shorter bar, faster swim.`;
